import React from 'react';
import { useSession } from '../contexts/SessionContext';
import { Clock, Activity, AlertTriangle, RefreshCw, LogOut } from 'lucide-react';

export default function SessionTestPage() {
  const { sessionState, continueSession, logoutNow, resetSession, handleActivity } = useSession();

  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const getStatusColor = () => {
    if (sessionState.isWarning) return 'text-red-600';
    if (sessionState.isIdle) return 'text-yellow-600';
    return 'text-green-600';
  };

  const getStatusIcon = () => {
    if (sessionState.isWarning) return <AlertTriangle className='h-5 w-5' />;
    if (sessionState.isIdle) return <Clock className='h-5 w-5' />;
    return <Activity className='h-5 w-5' />;
  };

  const getStatusText = () => {
    if (sessionState.isWarning) return 'Warning - Session will expire soon';
    if (sessionState.isIdle) return 'Idle - Session will expire if inactive';
    return 'Active - Session is running normally';
  };

  return (
    <div className='min-h-screen bg-gray-50 dark:bg-gray-900 py-8'>
      <div className='max-w-4xl mx-auto px-4 sm:px-6 lg:px-8'>
        <div className='bg-white dark:bg-gray-800 shadow rounded-lg'>
          <div className='px-6 py-4 border-b border-gray-200 dark:border-gray-700'>
            <h1 className='text-2xl font-bold text-gray-900 dark:text-white'>
              Session Management Test Page
            </h1>
            <p className='mt-1 text-sm text-gray-500 dark:text-gray-400'>
              Test and monitor session management functionality
            </p>
          </div>

          <div className='p-6'>
            {/* Session Status */}
            <div className='mb-8'>
              <h2 className='text-lg font-semibold text-gray-900 dark:text-white mb-4'>
                Session Status
              </h2>
              <div className='bg-gray-50 dark:bg-gray-700 rounded-lg p-4'>
                <div className='flex items-center space-x-3 mb-4'>
                  <div className={`flex items-center space-x-2 ${getStatusColor()}`}>
                    {getStatusIcon()}
                    <span className='font-medium'>{getStatusText()}</span>
                  </div>
                </div>

                <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                  <div className='bg-white dark:bg-gray-800 rounded-lg p-4'>
                    <div className='flex items-center justify-between'>
                      <span className='text-sm font-medium text-gray-500 dark:text-gray-400'>
                        Total Session Time Remaining
                      </span>
                      <span className='text-2xl font-mono font-bold text-gray-900 dark:text-white'>
                        {formatTime(sessionState.timeRemaining)}
                      </span>
                    </div>
                  </div>

                  {sessionState.isWarning && (
                    <div className='bg-red-50 dark:bg-red-900/20 rounded-lg p-4'>
                      <div className='flex items-center justify-between'>
                        <span className='text-sm font-medium text-red-600 dark:text-red-400'>
                          Warning Countdown
                        </span>
                        <span className='text-2xl font-mono font-bold text-red-600 dark:text-red-400'>
                          {formatTime(sessionState.warningTimeRemaining)}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Session Controls */}
            <div className='mb-8'>
              <h2 className='text-lg font-semibold text-gray-900 dark:text-white mb-4'>
                Session Controls
              </h2>
              <div className='flex flex-wrap gap-4'>
                <button
                  onClick={resetSession}
                  className='inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
                >
                  <RefreshCw className='h-4 w-4 mr-2' />
                  Reset Session
                </button>

                <button
                  onClick={handleActivity}
                  className='inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
                >
                  <Activity className='h-4 w-4 mr-2' />
                  Simulate Activity
                </button>

                {sessionState.isWarning && (
                  <button
                    onClick={continueSession}
                    className='inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500'
                  >
                    <RefreshCw className='h-4 w-4 mr-2' />
                    Continue Session
                  </button>
                )}

                <button
                  onClick={logoutNow}
                  className='inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500'
                >
                  <LogOut className='h-4 w-4 mr-2' />
                  Logout Now
                </button>
              </div>
            </div>

            {/* Session Details */}
            <div className='mb-8'>
              <h2 className='text-lg font-semibold text-gray-900 dark:text-white mb-4'>
                Session Details
              </h2>
              <div className='bg-gray-50 dark:bg-gray-700 rounded-lg p-4'>
                <dl className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                  <div>
                    <dt className='text-sm font-medium text-gray-500 dark:text-gray-400'>
                      Session Active
                    </dt>
                    <dd className='mt-1 text-sm text-gray-900 dark:text-white'>
                      {sessionState.isActive ? 'Yes' : 'No'}
                    </dd>
                  </div>

                  <div>
                    <dt className='text-sm font-medium text-gray-500 dark:text-gray-400'>
                      Idle State
                    </dt>
                    <dd className='mt-1 text-sm text-gray-900 dark:text-white'>
                      {sessionState.isIdle ? 'Yes' : 'No'}
                    </dd>
                  </div>

                  <div>
                    <dt className='text-sm font-medium text-gray-500 dark:text-gray-400'>
                      Warning State
                    </dt>
                    <dd className='mt-1 text-sm text-gray-900 dark:text-white'>
                      {sessionState.isWarning ? 'Yes' : 'No'}
                    </dd>
                  </div>

                  <div>
                    <dt className='text-sm font-medium text-gray-500 dark:text-gray-400'>
                      Time Remaining (seconds)
                    </dt>
                    <dd className='mt-1 text-sm text-gray-900 dark:text-white'>
                      {sessionState.timeRemaining}
                    </dd>
                  </div>
                </dl>
              </div>
            </div>

            {/* Instructions */}
            <div className='bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4'>
              <h3 className='text-sm font-medium text-blue-800 dark:text-blue-200 mb-2'>
                How to Test Session Management:
              </h3>
              <ul className='text-sm text-blue-700 dark:text-blue-300 space-y-1'>
                <li>
                  • <strong>Idle Test:</strong> Wait 30 minutes without activity to trigger idle
                  warning
                </li>
                <li>
                  • <strong>Activity Test:</strong> Click "Simulate Activity" to reset idle timer
                </li>
                <li>
                  • <strong>Warning Test:</strong> When warning appears, test "Continue Session" and
                  "Logout Now"
                </li>
                <li>
                  • <strong>Session Test:</strong> Wait 1 hour total to test session expiration
                </li>
                <li>
                  • <strong>Reset Test:</strong> Use "Reset Session" to restart all timers
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
